import React from 'react';
import { Box, Skeleton, Stack, TableCell, TableRow, Typography } from '@mui/material';
/** Matches MUI Chip size="small" in ranking Marcas column. */
export const MARCAS_CHIP_SKELETON_WIDTH = 36;
export const MARCAS_CHIP_SKELETON_HEIGHT = 24;

export const MarcasChipSkeleton: React.FC<{ align?: 'left' | 'right' | 'center' }> = ({
    align = 'right',
}) => (
    <Skeleton
        variant="rounded"
        width={MARCAS_CHIP_SKELETON_WIDTH}
        height={MARCAS_CHIP_SKELETON_HEIGHT}
        sx={{
            borderRadius: '16px',
            ...(align === 'right' ? { ml: 'auto' } : align === 'center' ? { mx: 'auto' } : {}),
        }}
    />
);

/** Shared min-height so summary cards do not shift when Marcas count loads. */
export const SUMMARY_CARD_PAPER_SX = {
    p: 3,
    textAlign: 'center' as const,
    width: '100%',
    minHeight: 120,
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
};

/** Inner padding for Explorer list/table panels (matches global MuiList theme). */
export const EXPLORER_PANEL_CONTENT_SX = {
    pt: 1.5,
    pb: 1.5,
    pl: 2,
    pr: 2,
} as const;

/** Reset global MuiList chrome when nested inside Paper (matches summary/table panels). */
export const EXPLORER_PANEL_LIST_SX = {
    border: 'none',
    boxShadow: 'none',
    bgcolor: 'transparent',
    ...EXPLORER_PANEL_CONTENT_SX,
} as const;

/** h3-sized value placeholder. Avoid nesting Skeleton in Typography (overlaps the wave). */
export const SummaryValueSkeleton: React.FC = () => (
    <Skeleton
        variant="rounded"
        animation="wave"
        width={72}
        height={28}
        sx={{ mx: 'auto', maxWidth: '80%', borderRadius: 1 }}
    />
);

/** Dual metric placeholder (views + interactions / peak + avg). */
export const SummaryMetricPairSkeleton: React.FC = () => (
    <Stack direction="row" spacing={2.5} justifyContent="center" alignItems="flex-start">
        <Box sx={{ textAlign: 'center', minWidth: 0 }}>
            <Skeleton
                variant="rounded"
                animation="wave"
                width={56}
                height={28}
                sx={{ mx: 'auto', borderRadius: 1 }}
            />
            <Skeleton
                variant="rounded"
                animation="wave"
                width={48}
                height={12}
                sx={{ mx: 'auto', mt: 0.75, borderRadius: 0.5 }}
            />
        </Box>
        <Box sx={{ textAlign: 'center', minWidth: 0 }}>
            <Skeleton
                variant="rounded"
                animation="wave"
                width={56}
                height={28}
                sx={{ mx: 'auto', borderRadius: 1 }}
            />
            <Skeleton
                variant="rounded"
                animation="wave"
                width={48}
                height={12}
                sx={{ mx: 'auto', mt: 0.75, borderRadius: 0.5 }}
            />
        </Box>
    </Stack>
);

export const RankingProgramRowSkeleton: React.FC = () => (
    <Stack direction="row" spacing={2} alignItems="center">
        <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: 1 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton variant="text" width="75%" height={22} sx={{ maxWidth: 200 }} />
            <Skeleton variant="text" width="55%" height={18} sx={{ maxWidth: 140 }} />
        </Box>
    </Stack>
);

export const RankingCanalesRowSkeleton: React.FC = () => (
    <Stack direction="row" spacing={2} alignItems="center">
        <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: 1 }} />
        <Skeleton variant="text" width="70%" height={22} sx={{ maxWidth: 220 }} />
    </Stack>
);

export const RankingFavoriteRowSkeleton: React.FC = () => (
    <Stack direction="row" spacing={1} alignItems="center">
        <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 1 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton variant="text" width="80%" height={20} sx={{ maxWidth: 160 }} />
            <Skeleton variant="text" width="60%" height={16} sx={{ maxWidth: 120 }} />
        </Box>
    </Stack>
);

/** Channel detail programs table — avatar + single program name, 8 columns. */
export const PodcastDetailProgramRowSkeleton: React.FC = () => (
    <TableRow>
        <TableCell align="center">
            <Skeleton width={24} height={20} />
        </TableCell>
        <TableCell>
            <RankingCanalesRowSkeleton />
        </TableCell>
        <TableCell align="right">
            <Skeleton width={40} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right">
            <Skeleton width={56} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right">
            <Skeleton width={48} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right">
            <Skeleton width={48} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right">
            <Skeleton width={56} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right">
            <MarcasChipSkeleton />
        </TableCell>
    </TableRow>
);

/** Program detail — Auspiciadores row (32px avatar, text, mention chip, expand). */
export const ProgramDetailBrandRowSkeleton: React.FC = () => (
    <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{
            py: 1,
            px: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
        }}
    >
        <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 1 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton variant="text" width="50%" height={20} sx={{ maxWidth: 200 }} />
            <Skeleton variant="text" width="35%" height={16} sx={{ maxWidth: 120, mt: 0.5 }} />
        </Box>
        <Skeleton variant="rounded" width={88} height={24} sx={{ borderRadius: '16px', flexShrink: 0 }} />
        <Skeleton variant="circular" width={28} height={28} />
    </Stack>
);

export const LIVESTREAM_DETAIL_TABLE_MIN_WIDTH = 720;

/** A minmax(0, 1fr) grid has zero min-content width, so the cell never grows to fit a long title. */
export const LIVESTREAM_DETAIL_TITLE_WRAP_SX = {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
} as const;

export type LivestreamDetailColKey =
    | 'fecha'
    | 'title'
    | 'peak'
    | 'duration'
    | 'avg'
    | 'views'
    | 'actions';

/**
 * Auto table layout: every column shrinks to its content; title takes the rest.
 * Wrap title content in LIVESTREAM_DETAIL_TITLE_WRAP_SX so long titles truncate instead of widening the table.
 */
export function livestreamDetailColSx(key: LivestreamDetailColKey) {
    switch (key) {
        case 'title':
            return { width: '100%', minWidth: 200 } as const;
        case 'fecha':
            return { width: 124, minWidth: 124, whiteSpace: 'nowrap' } as const;
        case 'actions':
            return { width: '1%', whiteSpace: 'nowrap', px: 1 } as const;
        default:
            return { width: '1%', whiteSpace: 'nowrap' } as const;
    }
}

/** Livestream detail table — fecha + title (left), metrics + actions (right). */
export const LivestreamDetailTableRowSkeleton: React.FC<{
    showSubtitleChip?: boolean;
}> = ({ showSubtitleChip = true }) => (
    <TableRow>
        <TableCell align="left" sx={livestreamDetailColSx('fecha')}>
            <Stack spacing={0.25}>
                <Skeleton width={72} height={18} />
                <Skeleton width={52} height={16} />
            </Stack>
        </TableCell>
        <TableCell align="left" sx={livestreamDetailColSx('title')}>
            <Box sx={{ minWidth: 0 }}>
                <Skeleton variant="text" width="92%" height={22} sx={{ mb: showSubtitleChip ? 0.5 : 0 }} />
                {showSubtitleChip ? (
                    <Skeleton variant="rounded" width={88} height={22} sx={{ borderRadius: '16px' }} />
                ) : null}
            </Box>
        </TableCell>
        <TableCell align="right" sx={livestreamDetailColSx('views')}>
            <Skeleton width={56} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right" sx={livestreamDetailColSx('avg')}>
            <Skeleton width={48} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right" sx={livestreamDetailColSx('peak')}>
            <Skeleton width={48} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right" sx={livestreamDetailColSx('duration')}>
            <Skeleton width={40} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right" sx={livestreamDetailColSx('actions')}>
            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                <Skeleton variant="circular" width={28} height={28} />
                <Skeleton variant="circular" width={28} height={28} />
            </Stack>
        </TableCell>
    </TableRow>
);

/** Program detail episodes table — fecha + title (left), metrics + play + expand (right). */
export const ProgramDetailEpisodeTableRowSkeleton: React.FC = () => (
    <TableRow>
        <TableCell align="left" sx={{ width: 108 }}>
            <Stack spacing={0.25}>
                <Skeleton width={72} height={18} />
                <Skeleton width={52} height={16} />
            </Stack>
        </TableCell>
        <TableCell align="left">
            <Skeleton variant="text" width="85%" height={22} sx={{ maxWidth: 320 }} />
        </TableCell>
        <TableCell align="right">
            <Skeleton width={40} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right">
            <Skeleton width={48} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right">
            <Skeleton width={40} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right">
            <Skeleton width={48} height={20} sx={{ ml: 'auto' }} />
        </TableCell>
        <TableCell align="right" sx={{ width: 88, px: 0.5 }}>
            <Stack direction="row" justifyContent="flex-end" spacing={0}>
                <Skeleton variant="circular" width={28} height={28} />
                <Skeleton variant="circular" width={28} height={28} />
            </Stack>
        </TableCell>
    </TableRow>
);

/** Program detail — Episodio row (title + metadata line, no thumbnail). */
export const ProgramDetailEpisodeRowSkeleton: React.FC = () => (
    <Box
        sx={{
            py: 1.5,
            px: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
        }}
    >
        <Skeleton variant="text" width="78%" height={20} sx={{ maxWidth: 420, mb: 0.75 }} />
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Skeleton variant="text" width={80} height={16} />
            <Skeleton variant="text" width={48} height={16} />
            <Skeleton variant="text" width={56} height={16} />
        </Stack>
    </Box>
);
